import type { UserLocalStorage } from '../../types/localStorage';

import { useQueryClient } from '@tanstack/react-query';
import { use, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useLocalStorageState from 'use-local-storage-state';

import { getEnv } from '../../lib/env';
import { removeDowngradedFlags } from '../../lib/flotiq-client/api-helpers';
import { getTestProps } from '../../lib/helpers';

import { useUser } from '../../hooks/api';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import { useModals } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import DowngradePlanModalContent from '../DowngradePlanModalContent/DowngradePlanModalContent';

export const useDowngradeModals = (testId?: string) => {
  const { t } = useTranslation();
  const jwt = useToken();
  const modal = useModals();
  const [user] = useLocalStorageState<UserLocalStorage>('cms.user');
  const { isAdmin } = use(UserContext);
  const [userData] = useUser(user?.data?.id);
  const { space } = useSelectedSpace();
  const queryClient = useQueryClient();

  const showTrialEndedModal = useCallback(
    (spaceId: string) =>
      modal({
        id: 'trial-ended',
        content: (
          <DowngradePlanModalContent
            type="downgraded"
            {...getTestProps(testId, 'trial-ended', 'testId')}
          />
        ),
        size: 'xl',
        buttons: [
          {
            key: 'upgrade',
            color: 'blue',
            label: isAdmin ? t('Trial.UpgradeNow') : t('Trial.ComparePlans'),
            ...(isAdmin
              ? { link: `/space/upgrade/${spaceId}` }
              : {
                  props: { target: '_blank' },
                  link: getEnv('VITE_PRICING') || '',
                }),
          },
          {
            key: 'free',
            color: 'blueBordered',
            label: t('Trial.Ended.FreePlan'),
            onClick: () => {
              removeDowngradedFlags(queryClient, userData, jwt);
            },
          },
        ],
      }),
    [isAdmin, jwt, modal, queryClient, t, testId, userData],
  );

  const showUserUnassignedModal = useCallback(
    (spaceId: string) =>
      modal({
        id: 'user-unassigned',
        content: (
          <DowngradePlanModalContent
            type="unassigned"
            {...getTestProps(testId, 'user-unassigned', 'testId')}
          />
        ),
        size: 'xl',
        buttons: isAdmin
          ? [
              {
                key: 'upgrade',
                color: 'blue',
                link: `/space/upgrade/${spaceId}`,
                label: t('Trial.UpgradeNow'),
              },
              {
                key: 'free',
                color: 'blueBordered',
                label: t('Trial.SeatLimit.ManageTeam'),
                link: `/space/edit/${spaceId}`,
                onClick: () => {
                  removeDowngradedFlags(queryClient, userData, jwt);
                },
              },
            ]
          : [
              {
                key: 'upgrade',
                color: 'blue',
                link: getEnv('VITE_PRICING') || '',
                label: t('Trial.ComparePlans'),
                onClick: () => {
                  removeDowngradedFlags(queryClient, userData, jwt);
                },
                props: {
                  target: '_blank',
                },
              },
            ],
      }),
    [isAdmin, jwt, modal, queryClient, t, testId, userData],
  );

  const checkNotices = useCallback(() => {
    let downgradedNotices = userData?.config?.downgraded_notice || [];
    if (!Array.isArray(downgradedNotices)) {
      downgradedNotices =
        typeof downgradedNotices === 'object'
          ? Object.values(downgradedNotices)
          : [];
    }

    const trialEndedNotice = downgradedNotices.find(
      ({ type, spaceId }) => type === 'downgraded' && spaceId === space,
    );

    const userUnassignedNotice = downgradedNotices.find(
      ({ type }) => type === 'unassigned',
    );

    if (userUnassignedNotice) {
      showUserUnassignedModal(userUnassignedNotice.spaceId);
    } else if (trialEndedNotice) {
      showTrialEndedModal(trialEndedNotice.spaceId);
    }
  }, [
    showTrialEndedModal,
    showUserUnassignedModal,
    space,
    userData?.config?.downgraded_notice,
  ]);

  return checkNotices;
};
